/**
 * 性能优化功能开关
 * 用于渐进式启用和快速回滚
 *
 * @module performance/features
 * @created 2026-04-14 - Phase 8 Wave 8.1
 */

/**
 * 性能优化功能开关配置
 */
export const PERFORMANCE_FEATURES = {
  // Wave 8.1: 分辨率限制
  /** 是否启用分辨率限制功能 */
  ENABLE_RESOLUTION_LIMIT: true,
  /** 默认字符数上限 */
  RESOLUTION_LIMIT_DEFAULT: 10000,

  // Wave 8.2: 性能监控
  /** 是否启用性能监控 */
  ENABLE_PERF_MONITOR: true,
  /** 是否显示FPS UI */
  PERF_MONITOR_FPS_DISPLAY: true,

  // Wave 8.3: 灰度缓存
  /** 是否启用灰度预计算缓存 */
  ENABLE_GRAYSCALE_CACHE: true,

  // Wave 8.4: 字符缓存
  /** 是否启用字符预渲染缓存 */
  ENABLE_CHAR_CACHE: false,  // 默认关闭，稳定后开启

  // 全局开关
  /** 一键禁用所有优化 */
  DISABLE_ALL_PERF_OPTIMIZATIONS: false,
} as const

export type PerformanceFeatureKey = keyof typeof PERFORMANCE_FEATURES

/**
 * 检查某个功能是否启用
 *
 * @param feature - 功能名称
 * @returns 是否启用
 */
export function isFeatureEnabled(feature: PerformanceFeatureKey): boolean {
  // 全局开关优先
  if (PERFORMANCE_FEATURES.DISABLE_ALL_PERF_OPTIMIZATIONS) {
    return false
  }

  const value = PERFORMANCE_FEATURES[feature]

  // 只返回布尔类型的值，数值类型返回true（表示功能启用）
  if (typeof value === 'boolean') {
    return value
  }

  // 数值类型（如 RESOLUTION_LIMIT_DEFAULT）表示配置值，功能本身启用
  return true
}

/**
 * 获取分辨率限制的默认字符数上限
 *
 * @returns 字符数上限
 */
export function getDefaultMaxChars(): number {
  return PERFORMANCE_FEATURES.RESOLUTION_LIMIT_DEFAULT
}

/**
 * 检查分辨率限制功能是否启用
 *
 * @returns 是否启用
 */
export function isResolutionLimitEnabled(): boolean {
  return isFeatureEnabled('ENABLE_RESOLUTION_LIMIT')
}

/**
 * 检查性能监控功能是否启用
 *
 * @returns 是否启用
 */
export function isPerfMonitorEnabled(): boolean {
  return isFeatureEnabled('ENABLE_PERF_MONITOR')
}

/**
 * 检查FPS显示是否启用
 *
 * @returns 是否启用
 */
export function isFPSDisplayEnabled(): boolean {
  return isFeatureEnabled('PERF_MONITOR_FPS_DISPLAY')
}

/**
 * 检查灰度缓存是否启用
 *
 * @returns 是否启用
 */
export function isGrayscaleCacheEnabled(): boolean {
  return isFeatureEnabled('ENABLE_GRAYSCALE_CACHE')
}

/**
 * 检查字符缓存是否启用
 *
 * @returns 是否启用
 */
export function isCharCacheEnabled(): boolean {
  return isFeatureEnabled('ENABLE_CHAR_CACHE')
}